import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Send, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "cvfolks_sarah_session_v2";

const SARAH_AVATAR =
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2.8&w=160&h=160&q=80";

const GREETING = "Hello! 👋 Do you need help creating a new CV?";

const QUICK_REPLIES = ["Yes please", "Tell me more", "What do you offer?"];

// One-tap choices shown right after Sarah's two-path offer (post name/email step).
const OFFER_CHIPS = ["AI CV Builder £9.99", "Hire a writer £50"];
const OFFER_RE = /would you like info on our ai cv builder/i;

const PACKAGES = {
  "ai-cv-builder": { name: "AI CV Builder", price: 9.99 },
  "standard-cv": { name: "Standard CV", price: 50 },
  "standard-package": { name: "Standard Package", price: 95 },
  "premium-cv": { name: "Premium CV", price: 125 },
  "premium-package": { name: "Premium Package", price: 155 },
};

const BUY_RE = /\[BUY:([a-z-]+)\]/i;
const WHATSAPP_RE = /\[WHATSAPP\]/i;
const DEFAULT_WHATSAPP_PHONE = "447448457089";
const DEFAULT_WHATSAPP_GREETING =
  "Hi CV Folks, I'd like to speak to a career consultant.";

function parseReply(text) {
  if (!text) return { text: "", buy: null, whatsapp: false };
  let cleaned = text;
  const buyMatch = cleaned.match(BUY_RE);
  const hasWa = WHATSAPP_RE.test(cleaned);
  cleaned = cleaned.replace(BUY_RE, "").replace(WHATSAPP_RE, "").trim();
  let buy = null;
  if (buyMatch) {
    const pkgId = buyMatch[1].toLowerCase();
    const pkg = PACKAGES[pkgId];
    if (pkg) buy = { id: pkgId, name: pkg.name, price: pkg.price };
  }
  return { text: cleaned, buy, whatsapp: hasWa };
}

function newSessionId() {
  return "ec_" + Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && d.session_id) return d;
  } catch (_) { /* ignore corrupt localStorage */ }
  return null;
}

function saveState(s) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

function BuyButton({ buy }) {
  return (
    <a
      href={`/checkout/${buy.id}`}
      data-testid={`emma-buy-${buy.id}`}
      className="emma-fade-in mt-2 inline-flex items-center gap-2 text-[13px] font-semibold px-4 py-2.5 rounded-full bg-foreground text-background hover:bg-brand transition shadow-md"
    >
      Buy {buy.name} — £{buy.price}
      <ArrowRight className="h-3.5 w-3.5" />
    </a>
  );
}

function WhatsAppButton({ url }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="emma-whatsapp-cta"
      className="emma-fade-in mt-2 inline-flex items-center gap-2 text-[13px] font-semibold px-4 py-2.5 rounded-full bg-[#25D366] text-white hover:brightness-95 transition shadow-md"
    >
      <svg viewBox="0 0 32 32" className="h-4 w-4 fill-current">
        <path d="M19.11 17.35c-.28-.14-1.68-.83-1.94-.92-.26-.09-.45-.14-.63.14-.19.28-.72.92-.88 1.11-.16.19-.32.21-.6.07-.28-.14-1.19-.44-2.27-1.4-.84-.75-1.4-1.68-1.57-1.96-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.87-2.08-.23-.55-.46-.47-.63-.48-.16 0-.35-.02-.53-.02-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.35s1 2.72 1.14 2.91c.14.19 1.97 3.01 4.77 4.22.67.29 1.19.46 1.6.59.67.21 1.28.18 1.76.11.54-.08 1.68-.68 1.92-1.34.24-.66.24-1.22.17-1.34-.07-.12-.26-.19-.53-.33zM16.02 5.33c-5.9 0-10.7 4.79-10.7 10.68 0 1.88.5 3.72 1.44 5.34l-1.53 5.6 5.72-1.5c1.56.85 3.32 1.3 5.06 1.3h.01c5.9 0 10.7-4.79 10.7-10.68 0-2.85-1.11-5.53-3.13-7.55-2.02-2.02-4.71-3.14-7.57-3.19z" />
      </svg>
      Tap here to WhatsApp us
      <ArrowRight className="h-3.5 w-3.5" />
    </a>
  );
}

function Bubble({ role, text, buy, whatsapp, whatsappUrl, animate = false }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex w-full ${isUser ? "justify-end" : "justify-start"} items-end gap-2`}
      data-testid={`emma-msg-${role}`}
    >
      {!isUser && (
        <img
          src={SARAH_AVATAR}
          alt=""
          className="h-7 w-7 rounded-full object-cover ring-1 ring-brand/30 shrink-0"
        />
      )}
      <div className="max-w-[82%] flex flex-col items-start">
        {text && (
          <div
            className={`rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap shadow-sm ${
              isUser
                ? "bg-brand text-white rounded-br-sm"
                : "bg-white text-foreground border border-brand/15 rounded-bl-sm"
            } ${animate ? "emma-fade-in" : ""}`}
          >
            {text}
          </div>
        )}
        {!isUser && buy && <BuyButton buy={buy} />}
        {!isUser && whatsapp && <WhatsAppButton url={whatsappUrl} />}
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className="flex justify-start items-end gap-2" data-testid="emma-typing">
      <img
        src={SARAH_AVATAR}
        alt=""
        className="h-7 w-7 rounded-full object-cover ring-1 ring-brand/30"
      />
      <div className="bg-white border border-brand/15 rounded-2xl rounded-bl-sm px-3.5 py-3 shadow-sm">
        <div className="flex gap-1.5">
          <span className="emma-dot" />
          <span className="emma-dot" style={{ animationDelay: "0.15s" }} />
          <span className="emma-dot" style={{ animationDelay: "0.3s" }} />
        </div>
      </div>
    </div>
  );
}

export default function EmmaChat() {
  const [state, setState] = useState(() => {
    const saved = loadState();
    return (
      saved || {
        session_id: newSessionId(),
        messages: [{ role: "assistant", text: GREETING }],
      }
    );
  });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState(
    `https://wa.me/${DEFAULT_WHATSAPP_PHONE}?text=${encodeURIComponent(DEFAULT_WHATSAPP_GREETING)}`
  );
  const scrollRef = useRef(null);

  // M9 — Fetch the admin-editable WhatsApp escalation number/greeting.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/settings/public`)
      .then(({ data }) => {
        if (cancelled) return;
        const esc = data?.escalation;
        if (!esc?.whatsapp_phone_e164) return;
        const digits = esc.whatsapp_phone_e164.replace(/\D/g, "");
        const greeting = esc.whatsapp_greeting || DEFAULT_WHATSAPP_GREETING;
        setWhatsappUrl(`https://wa.me/${digits}?text=${encodeURIComponent(greeting)}`);
      })
      .catch(() => { /* keep default */ });
    return () => { cancelled = true; };
  }, []);

  // Persist conversation
  useEffect(() => {
    saveState(state);
  }, [state]);

  // M6 — Re-hydrate from Mongo on mount if we have a session_id but only the
  // default greeting locally (fresh localStorage / cleared browser / another
  // device with the same session id). The backend is now the source of truth,
  // so we can pull the real transcript and slot it in before the visitor
  // resumes typing.
  useEffect(() => {
    let cancelled = false;
    const sid = state.session_id;
    const hasReal = state.messages.some((m) => m.role === "user");
    if (!sid || hasReal) return; // nothing to rehydrate
    (async () => {
      try {
        const { data } = await axios.get(`${API}/emma/messages/${sid}`);
        const msgs = data?.messages || [];
        if (!msgs.length || cancelled) return;
        const hydrated = [
          { role: "assistant", text: GREETING },
          ...msgs.map((m) => ({
            role: m.role === "assistant" ? "assistant" : "user",
            text: m.content || "",
            _nudge: !!m.is_nudge,
          })),
        ];
        setState((s) => ({ ...s, messages: hydrated }));
      } catch (_) { /* first-time visitor / offline — safe to ignore */ }
    })();
    return () => { cancelled = true; };
    // Intentionally run once — session_id is stable for the widget lifetime.
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [state.messages, sending, open]);

  // Broadcast panel-open state so global launchers (e.g. WhatsAppLauncher)
  // can hide themselves when Sarah is open — prevents overlap in the same
  // bottom-right corner.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("sarah:panel-state", { detail: { open } }));
  }, [open]);

  // Allow any button on the site to open Sarah's panel by dispatching a
  // "sarah:open" custom event on window. Used by CTAs like "Chat with Sarah"
  // in the Meet Sarah homepage section.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("sarah:open", handler);
    return () => window.removeEventListener("sarah:open", handler);
  }, []);

  // Unread-style launcher nudge REMOVED — the global floating Sarah
  // launcher was retired in favour of a WhatsApp launcher, so there is
  // nothing left to attach an unread badge to. The post-answer follow-up
  // nudge below still lives on because it appends into the panel itself.

  // Post-answer follow-up nudge — after 2 minutes without a user reply
  // (once the visitor has already engaged), Sarah drops ONE soft nudge into
  // the transcript. Idempotent per session both client-side (ref) and
  // server-side (`/api/emma/nudge` sets nudge_sent on the lead). Helps
  // recover visitors who bounce mid-conversation without being pushy.
  const followUpDone = useRef(false);
  useEffect(() => {
    if (followUpDone.current || sending) return;
    const msgs = state.messages;
    const hasUser = msgs.some((m) => m.role === "user");
    if (!hasUser) return;
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== "assistant") return;
    // Skip if the last assistant message is itself the nudge (rehydration)
    if (last._nudge) {
      followUpDone.current = true;
      return;
    }
    const t = setTimeout(async () => {
      if (followUpDone.current) return;
      followUpDone.current = true;
      try {
        const res = await axios.post(`${API}/emma/nudge`, {
          session_id: state.session_id,
        });
        const nudgeText =
          res?.data?.text ||
          "Still thinking? Happy to compare AI Builder vs a writer for you.";
        if (res?.data?.already_sent) return; // server-side idempotency
        setState((s) => ({
          ...s,
          messages: [
            ...s.messages,
            { role: "assistant", text: nudgeText, _nudge: true },
          ],
        }));
      } catch (_) {
        // On network failure, still show the nudge locally so the visitor
        // sees the follow-up. Non-persisted; no big deal.
        setState((s) => ({
          ...s,
          messages: [
            ...s.messages,
            {
              role: "assistant",
              text: "Still thinking? Happy to compare AI Builder vs a writer for you.",
              _nudge: true,
            },
          ],
        }));
      }
    }, 2 * 60 * 1000);
    return () => clearTimeout(t);
  }, [state.messages, sending, state.session_id, open]);

  const send = useCallback(
    async (text) => {
      const content = (text || draft).trim();
      if (!content || sending) return;
      setDraft("");
      setState((s) => ({
        ...s,
        messages: [...s.messages, { role: "user", text: content }],
      }));
      setSending(true);
      try {
        const res = await axios.post(`${API}/emma/chat`, {
          session_id: state.session_id,
          message: content,
        });
        const parsed = parseReply(res.data.reply);
        setState((s) => ({
          ...s,
          messages: [
            ...s.messages,
            { role: "assistant", text: parsed.text, buy: parsed.buy, whatsapp: parsed.whatsapp },
          ],
        }));
      } catch (err) {
        setState((s) => ({
          ...s,
          messages: [
            ...s.messages,
            {
              role: "assistant",
              text:
                "Sorry — I'm having a moment of technical trouble. Please try again in a few seconds.",
            },
          ],
        }));
      } finally {
        setSending(false);
      }
    },
    [draft, sending, state.session_id]
  );

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const hasUserSentAny = state.messages.some((m) => m.role === "user");
  const lastMsg = state.messages[state.messages.length - 1];
  const showOfferChips =
    !sending &&
    lastMsg?.role === "assistant" &&
    !lastMsg.buy &&
    OFFER_RE.test(lastMsg.text || "");

  return (
    <>
      {/* -------- Sarah AI chat (panel-only) --------
          The floating launcher has been retired — Sarah now opens ONLY when
          a page explicitly dispatches `sarah:open` (e.g. the "Ask Sarah"
          button on the AI CV Maker page). The main visitor entry-point is
          now the WhatsAppLauncher rendered at the App level. */}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="emma-panel"
          className="fixed z-50 bg-[#FFF9F1] shadow-2xl border border-brand/15 emma-panel rounded-3xl
                     bottom-24 right-3 left-3 h-[460px]
                     sm:left-auto sm:right-6 sm:bottom-24 sm:w-[320px]
                     md:h-[460px] md:w-[320px]
                     flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-brand to-brand-500 text-white flex items-center gap-3">
            <div className="relative shrink-0">
              <img
                src={SARAH_AVATAR}
                alt="Sarah"
                className="h-11 w-11 rounded-full object-cover ring-2 ring-white/60"
              />
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-400 ring-2 ring-brand" />
            </div>
            <div className="flex-1 leading-tight">
              <p className="font-semibold text-[15px]">Sarah</p>
              <p className="text-[11px] text-white/85">
                CV Folks · Career Consultant
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              data-testid="emma-close"
              className="h-8 w-8 rounded-full hover:bg-white/15 flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5"
            data-testid="emma-messages"
          >
            {state.messages.map((m, i) => (
              <Bubble
                key={i}
                role={m.role}
                text={m.text}
                buy={m.buy}
                whatsapp={m.whatsapp}
                whatsappUrl={whatsappUrl}
                animate={i === state.messages.length - 1}
              />
            ))}
            {sending && <Typing />}

            {/* Two-path offer chips — after the name/email step */}
            {showOfferChips && (
              <div className="flex flex-wrap gap-2 pt-1 pl-9">
                {OFFER_CHIPS.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    data-testid={`emma-offer-${q.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className="text-[11.5px] font-semibold px-3.5 py-2 rounded-full bg-brand text-white border border-brand hover:bg-brand-500 transition shadow-sm"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Quick replies — only before the visitor has typed anything */}
            {!hasUserSentAny && !sending && (
              <div className="flex flex-wrap gap-2 pt-1 pl-9">
                {QUICK_REPLIES.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    data-testid={`emma-quick-${q.replace(/\s+/g, "-").toLowerCase()}`}
                    className="text-[11.5px] font-medium px-3 py-1.5 rounded-full bg-white border border-brand/30 text-brand hover:bg-brand hover:text-white transition"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-brand/15 bg-white p-2.5">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="emma-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Type your reply…"
                className="flex-1 resize-none max-h-28 min-h-[40px] px-3 py-2 rounded-2xl border border-brand/20 bg-[#FFF9F1] text-[13.5px] outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/15"
              />
              <button
                onClick={() => send()}
                disabled={!draft.trim() || sending}
                data-testid="emma-send"
                aria-label="Send message"
                className="h-10 w-10 shrink-0 rounded-full bg-brand hover:bg-brand-500 text-white shadow-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[10px] text-foreground/45">
              Sarah · CV Folks Career Consultant
            </p>
          </div>
        </div>
      )}
    </>
  );
}
